import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import CenteredContainer from '../../app/common/CenteredContainer';
import CategoriesPage from '../../app/menu/categoriesPage';
import initializePage from '../../app/common/pageSetup';
import { NATIONAL } from '../../app/localization/constants';
import { isNational } from '../../app/graphql/gqlDataManipulation';
import { RootState } from '@/rootStateTypes';
import localizationService from '@/services/localizationService';
import { selectStore } from '@/localization/actions';
import { ReduxApplicationProps } from '@/ReduxApplication/types';
import { ErrorPage } from '@/ErrorPage';

const isLocalized = (
  state: RootState
): boolean => state.domain.localization.localizedStore !== NATIONAL;
const isLocalizedOnServer = (
  storeNumber: string
): boolean => !isNational(storeNumber);

/**
 * in the case that PhdApiEndpoints.GET_STORE fails the user will get stuck on /menu page since this page
 * does not depend on any information present on PhdApiEndpoints.GET_STORE and clickin on a specific category
 * will fail. That's why we are firing getStoreDetails again to prevent user to get stuck.
 */
type UseGetStoresDetailIfPreviousError =
  Pick<CategoryPageProps, 'storeNumber' | 'localizationToken' | 'occasion'>;

const useGetStoresDetailIfPreviousError = (
  { storeNumber, localizationToken, occasion }: UseGetStoresDetailIfPreviousError,
  isUserLocalized: boolean
) => {
  const [storeResult, setStore] = useState<ClientResult<StoreDetail>>({ result: undefined, error: false });
  const router = useRouter();
  const hadPrevPageError = !!router.query.error;
  const dispatch = useDispatch();

  useEffect(() => {
    if (isUserLocalized && (!storeNumber || !localizationToken || !occasion)) {
      setStore({ result: undefined, error: true });
      return;
    }

    if (hadPrevPageError) {
      localizationService.getStoreDetails(storeNumber, occasion, localizationToken, false)
        .then(({ error, result }) => {
          setStore({ error, result });
          dispatch(selectStore(storeNumber, occasion, result));
        })
        .catch(setStore);
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hadPrevPageError]);

  return storeResult;
};

type CategoryPageProps =
 Required<Pick<ReduxApplicationProps, 'storeNumber' | 'localizationToken' | 'occasion'>>;

const CategoryPage = (
  { storeNumber, localizationToken, occasion }: CategoryPageProps
): JSX.Element => {
  const isLocalizedInRedux = useSelector(isLocalized);
  const isUserLocalized = isLocalizedInRedux || isLocalizedOnServer(storeNumber);
  const hasGqlError = useSelector(
    (state: RootState): boolean => state.presentational.gqlErrors.categoriesError
  );

  const { error } = useGetStoresDetailIfPreviousError(
    { storeNumber, localizationToken, occasion },
    isUserLocalized
  );

  if (error || hasGqlError) {
    return <ErrorPage />;
  }

  return (
    <>
      <CenteredContainer>
        <CategoriesPage isLocalized={isUserLocalized} />
      </CenteredContainer>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => initializePage(ctx, 'menu');

export default CategoryPage;
